import { useUserSetupStore } from '@sib/auth';
import { DOCUMENTS_MAX_SIZE_GB } from '@sib/config';
import { useTranslation } from '@sib/i18n';
import { Button, cn, Tooltip } from '@sib/ui';
import { Upload } from '@sib/ui/icons';
import { UploadingFilesList, useUploadingFilesList } from '@spendbrain/common';
import { useFileManagerFileUploadStore } from '@spendbrain/stores/fileUploadStore';

interface FileManagerProps {
  closeFileManager: () => void;
}

export const FileManager = ({ closeFileManager }: FileManagerProps) => {
  const { t } = useTranslation();
  const fileUploadStore = useFileManagerFileUploadStore();
  const {
    getRootProps,
    isDragActive,
    getInputProps,
    uploadFiles,
    areFilesUploading,
    hasErrors,
    organizationId,
    organizationName,
    areThereAnyFiles,
  } = useUploadingFilesList({
    fileUploadStore,
    closeFileManager,
  });
  const hasGlobalAccess = useUserSetupStore(store => store.hasGlobalAccess);

  const hasOrganizationContext = Boolean(organizationId || organizationName);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex h-full w-full flex-col gap-7 overflow-hidden lg:flex-row">
        <div
          {...getRootProps()}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-2.5 text-center transition-colors sm:p-4',
            isDragActive ? 'border-primary' : 'border-muted-foreground'
          )}
        >
          <input {...getInputProps()} />
          <Upload className="text-primary" />
          <div className="my-7">
            <p className="text-primary text-lg font-medium">
              {t('fileManager.drag')}
            </p>
            <p className="text-muted-foreground">
              {t('fileManager.supported')}
            </p>
            <p className="text-muted-foreground">
              {t('fileManager.maxFileSize', {
                size: `${DOCUMENTS_MAX_SIZE_GB} GB`,
              })}
            </p>
          </div>
          <Button variant="outline">{t('fileManager.browser')}</Button>
        </div>
        <UploadingFilesList fileUploadStore={fileUploadStore} />
      </div>
      <div className="mt-4 flex justify-center">
        <Tooltip
          content={
            hasOrganizationContext
              ? ''
              : hasGlobalAccess
                ? t('fileManager.tooltips.uploadDisabledNoOrganization')
                : t('fileManager.tooltips.uploadDisabledContactSupport')
          }
          TooltipTriggerProps={{ asChild: true }}
        >
          <span>
            <Button
              onClick={uploadFiles}
              loading={areFilesUploading}
              disabled={
                hasErrors || !areThereAnyFiles || !hasOrganizationContext
              }
            >
              {t('fileManager.uploadFilesForOrganization', {
                name: organizationName,
                interpolation: { escapeValue: false },
              })}
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};
